import { Box } from "@mui/material";
import type { SxProps, Theme } from "@mui/material";
import { useEffect, useState, useSyncExternalStore } from "react";
import { useTranslation } from "react-i18next";
import {
  SITE_COLOR,
  SITE_MEDIA,
} from "src/domains/shared/constants/styles/site";
import { SITE_BUTTON_STYLES } from "../siteKit/siteButtonStyles";

/** 이 시간 안에 라운지 문서가 다 뜨지 않으면 못 연 것으로 본다 */
const LOUNGE_TIMEOUT_MS = 15_000;

type LoungeState = "loading" | "ready" | "failed";

const isOffline = () => !navigator.onLine;

const subscribeNothing = () => () => {};

/**
 * 하이드레이션이 끝난 뒤에만 참 — 서버가 그린 iframe 이 하이드레이션 전에 다 떠 버리면 `onLoad` 를 놓쳐
 * 멀쩡한 상담 창을 실패로 보게 되므로, iframe 은 클라이언트에서 만든다.
 */
const useHydrated = () =>
  useSyncExternalStore(
    subscribeNothing,
    () => true,
    () => false
  );

/**
 * 상담 창을 열었는지 판정한다 — 다른 출처 문서라 오류를 직접 알 수 없으므로 `load` 가 15초 안에 오지 않거나
 * 오프라인이면 못 연 것으로 본다. 못 연 뒤 연결이 돌아오면 스스로 다시 연다. `attempt` 는 다시 열 때마다 바뀌는 iframe 열쇠다.
 */
function useLoungeLoad() {
  const hydrated = useHydrated();
  const [attempt, setAttempt] = useState(0);
  const [state, setState] = useState<LoungeState>("loading");

  const retry = () => {
    setAttempt((count) => count + 1);
    setState("loading");
  };
  // 오프라인이면 브라우저 오류 문서가 떠도 load 가 온다
  const markLoaded = () => setState(isOffline() ? "failed" : "ready");

  useEffect(() => {
    if (!hydrated || state !== "loading") return;
    const timer = window.setTimeout(
      () => setState("failed"),
      isOffline() ? 0 : LOUNGE_TIMEOUT_MS
    );
    const fail = () => setState("failed");
    window.addEventListener("offline", fail);
    return () => {
      window.clearTimeout(timer);
      window.removeEventListener("offline", fail);
    };
  }, [hydrated, state, attempt]);

  useEffect(() => {
    if (state !== "failed") return;
    window.addEventListener("online", retry);
    return () => window.removeEventListener("online", retry);
  });

  return { hydrated, attempt, state, retry, markLoaded };
}

const LOUNGE_STYLES = {
  root: {
    position: "relative",
    "& iframe": {
      display: "block",
      width: "100%",
      height: "100%",
      border: 0,
    },
  },
  // 상담 창 자리를 덮는 안내 — 여는 중·못 열었을 때
  cover: {
    position: "absolute",
    inset: 0,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: "8px",
    padding: "32px 28px",
    textAlign: "center",
    background: SITE_COLOR.panel2,
  },
  spinner: {
    width: "28px",
    height: "28px",
    marginBottom: "6px",
    borderRadius: "50%",
    border: "3px solid rgba(29,116,255,.18)",
    borderTopColor: SITE_COLOR.blue,
    animation: "loungeSpin .9s linear infinite",
    "@keyframes loungeSpin": { to: { transform: "rotate(360deg)" } },
    [SITE_MEDIA.reducedMotion]: { animation: "none" },
  },
  loadingText: { margin: 0, fontSize: "15px", color: SITE_COLOR.ink3 },
  failedTitle: {
    margin: 0,
    fontSize: "19px",
    lineHeight: 1.4,
    fontWeight: 700,
    letterSpacing: "-.02em",
    color: SITE_COLOR.heading,
  },
  failedText: {
    margin: 0,
    fontSize: "15px",
    lineHeight: 1.6,
    color: SITE_COLOR.ink2,
    textWrap: "balance",
  },
  actions: {
    display: "grid",
    gap: "10px",
    width: "100%",
    maxWidth: "320px",
    marginTop: "20px",
  },
  // 휴대폰 화면 전체 상담 창에서는 엄지로 누르기 쉬운 56px
  action: {
    width: "100%",
    [SITE_MEDIA.phone]: { height: "56px", fontSize: "17px" },
  },
} as const;

export interface LoungeFrameProps {
  src: string;
  title: string;
  /** 상담 창을 못 열었을 때 대신 내미는 대표전화 */
  call: { tel: string; href: string };
  sx?: SxProps<Theme>;
}

/**
 * 채널톡 라운지 상담 창(marketing-service-pages): 뜨는 동안은 「여는 중」을, 못 열었으면(`useLoungeLoad`)
 * 빈 상자 대신 「다시 시도」와 대표전화를 보인다.
 */
export function LoungeFrame({ src, title, call, sx }: LoungeFrameProps) {
  const { t } = useTranslation("landing");
  const { hydrated, attempt, state, retry, markLoaded } = useLoungeLoad();

  return (
    <Box sx={[LOUNGE_STYLES.root, ...(Array.isArray(sx) ? sx : [sx])]}>
      {hydrated && state !== "failed" ? (
        <iframe key={attempt} src={src} title={title} onLoad={markLoaded}>
          {t("site.inquiry.chatFallback")}
        </iframe>
      ) : null}
      {state === "loading" ? (
        <Box role="status" sx={LOUNGE_STYLES.cover}>
          <Box aria-hidden="true" sx={LOUNGE_STYLES.spinner} />
          <Box component="p" sx={LOUNGE_STYLES.loadingText}>
            {t("site.inquiry.chatLoading")}
          </Box>
        </Box>
      ) : null}
      {state === "failed" ? (
        <Box role="alert" sx={LOUNGE_STYLES.cover}>
          <Box component="p" sx={LOUNGE_STYLES.failedTitle}>
            {t("site.inquiry.chatFailedTitle")}
          </Box>
          <Box component="p" sx={LOUNGE_STYLES.failedText}>
            {t("site.inquiry.chatFailedText")}
          </Box>
          <Box sx={LOUNGE_STYLES.actions}>
            <Box
              component="button"
              type="button"
              onClick={retry}
              sx={[SITE_BUTTON_STYLES.primaryLarge, LOUNGE_STYLES.action]}
            >
              {t("site.inquiry.chatRetry")}
            </Box>
            <Box
              component="a"
              href={call.href}
              sx={[SITE_BUTTON_STYLES.greyLarge, LOUNGE_STYLES.action]}
            >
              {t("site.inquiry.chatCall", { tel: call.tel })}
            </Box>
          </Box>
        </Box>
      ) : null}
    </Box>
  );
}
